import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { selectActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/weather-webcam-module.module.css";
import {
  BaseModuleProps,
  WeatherModuleSettings,
  WeatherWebcamModuleSettings,
  WebcamModuleSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getTranslatedModulesByParentId,
} from "../../utils/utils.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import WeatherModule from "./WeatherModule.js";
import WebcamModule from "./WebcamModule.js";

type Props = BaseModuleProps<WeatherWebcamModuleSettings>;

const WeatherWebcamModule: FunctionComponent<Props> = ({
  queries,
  pageId,
  isPreview,
  activeModuleId,
  isFirstOnPage,
  translatedModule,
  translatedModule: {
    settings: { layoutVariant },
    translation: { languageId },
  },
}) => {
  const site = useAppSelector(selectActiveSite);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const modules = useAppSelector((state) => state.modules);
  const webcamModules = getTranslatedModulesByParentId<WebcamModuleSettings>({
    modules,
    parentId: translatedModule.id,
    pageId,
    moduleType: "WebcamModule",
    languageId,
  });

  const weatherModules = getTranslatedModulesByParentId<WeatherModuleSettings>({
    modules,
    parentId: translatedModule.id,
    pageId,
    moduleType: "WeatherModule",
    languageId,
  });
  const weatherModule = weatherModules.length ? weatherModules[0] : undefined;

  return (
    <ModuleWithHeadings
      title={undefined}
      subtitle={undefined}
      id={translatedModule.id}
      className={styles.WeatherWebcamModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      {isPreview && !weatherModule && !webcamModules.length && (
        <div
          className={ClassNames(
            moduleBaseStyles.Module__Wrapper,
            styles.WeatherWebcamModule__Placeholder,
          )}
        >
          <p>Fügen Sie ein Wetter- oder Webcam-Modul hinzu</p>
        </div>
      )}

      <div
        className={ClassNames(
          styles.WeatherWebcamModule__Inner,
          moduleBaseStyles.Module__Wrapper,
          {
            [styles["WeatherWebcamModule__Inner--horizontal"]]:
              layoutVariant === "horizontal" && queries["Query--large"],
            [styles["WeatherWebcamModule__Inner--vertical"]]:
              layoutVariant === "vertical" || !queries["Query--large"],
            [moduleBaseStyles.Module__Wrapper]: layoutVariant === "vertical",
          },
        )}
      >
        {weatherModule && (
          <div className={styles.WeatherWebcamModule__Weather}>
            <WeatherModule
              isActive={weatherModule.id === activeModuleId}
              translatedModule={weatherModule}
              queries={queries}
              pageId={pageId}
              isPreview={isPreview}
              activeModuleId={activeModuleId}
              isFirstOnPage={isFirstOnPage}
              scheme={scheme}
            />
          </div>
        )}

        {!!webcamModules.length && (
          <div className={styles.WeatherWebcamModule__Webcams}>
            {webcamModules.map((webcamModule) => (
              <WebcamModule
                key={webcamModule.id}
                isActive={webcamModule.id === activeModuleId}
                translatedModule={webcamModule}
                queries={queries}
                pageId={pageId}
                isPreview={isPreview}
                activeModuleId={activeModuleId}
                isFirstOnPage={weatherModule ? false : isFirstOnPage}
                scheme={scheme}
              />
            ))}
          </div>
        )}
      </div>
    </ModuleWithHeadings>
  );
};

export default WeatherWebcamModule;
